/**
 * Breadcrumb (STORY-008-006). Derived from useLocation().pathname + a
 * path→label map built from ADMIN_NAV (navConfig is the single source of truth
 * for section labels — no duplicated segment table to drift). The app uses a
 * declarative <Routes> tree, so useMatches()/route `handle` are unavailable
 * (see plan R1); the trail is resolved purely from the pathname. Dynamic id
 * segments (anything not in ADMIN_NAV, e.g. `/admin/sellers/:id`) resolve to
 * the AdminBreadcrumbProvider leaf override that detail pages set via
 * useSetBreadcrumbLeaf (e.g. a seller name). --g-* tokens only.
 */
import {
  createContext, useContext, useMemo, useState,
  type ReactElement, type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ADMIN_NAV } from "@/features/admin/navConfig";

interface BreadcrumbCtx { leafLabel: string | null; setLeafLabel: (v: string | null) => void; }
const Ctx = createContext<BreadcrumbCtx | null>(null);

export function AdminBreadcrumbProvider({ children }: { children: ReactNode }): ReactElement {
  const [leafLabel, setLeafLabel] = useState<string | null>(null);
  const value = useMemo(() => ({ leafLabel, setLeafLabel }), [leafLabel]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSetBreadcrumbLeaf(): (label: string | null) => void {
  const ctx = useContext(Ctx);
  return ctx?.setLeafLabel ?? (() => undefined);
}

/**
 * Full admin path → admin.json i18n key, derived from ADMIN_NAV (keyed by each
 * item's `to`). Built once at module load. A path absent here is a dynamic
 * segment (id) that resolves via the leaf override.
 */
const PATH_LABEL_KEY: Record<string, string> = {};
for (const group of ADMIN_NAV) {
  for (const item of group.items) {
    PATH_LABEL_KEY[item.to] = item.labelKey;
  }
}

export function Breadcrumb(): ReactElement | null {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { pathname } = useLocation();
  const ctx = useContext(Ctx);

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "admin") return null;

  const crumbs: { key: string; label: string; to: string }[] = [
    { key: "admin", label: t("breadcrumb.admin"), to: "/admin" },
  ];
  let acc = "/admin";
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i] ?? "";
    acc += `/${seg}`;
    const labelKey = PATH_LABEL_KEY[acc];
    crumbs.push({
      key: acc,
      label: labelKey ? t(labelKey) : (ctx?.leafLabel ?? seg),
      to: acc,
    });
  }

  return (
    <nav
      data-testid="admin-breadcrumb"
      aria-label={t("breadcrumb.ariaLabel")}
      className="flex items-center gap-1 text-sm text-[var(--g-secondary)]"
    >
      {crumbs.map((c, i) => (
        <span key={c.key} className="flex items-center gap-1">
          {i > 0 ? <span aria-hidden className="text-[var(--g-hairline)]">/</span> : null}
          {i < crumbs.length - 1 ? (
            <Link to={c.to} className="rounded hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]">
              {c.label}
            </Link>
          ) : (
            <span aria-current="page" className="font-medium text-foreground">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export default Breadcrumb;
